import { useEffect, useMemo, useState } from 'react';
import { useSubscriptionContext } from '../../../context/SubscriptionContext';
import { usePlanPayment, useFoundationPackCheckout } from '../../../hooks/usePlanPayment';
import FoundationPackPopup from '../../pricing/FoundationPackPopup';
import PricingPlansCarousel from '../../pricing/PricingPlansCarousel';
import OfferBar from '../OfferBar/OfferBar';
import FeatureBar from '../FeatureBar/FeatureBar';
import learningplan from '../../../assets/images/learningPlan.svg';

function PricingSection({ plansByCategory, loading }) {
  const { isPlanSubscribed, refetch } = useSubscriptionContext();
  const {
    startPayment,
    payingPlanId,
    paymentError,
    paymentSuccess,
    clearPaymentFeedback,
  } = usePlanPayment({ onSuccess: refetch });

  const tabs = useMemo(() => {
    if (!plansByCategory) return [];
    return Object.values(plansByCategory).map((group) => ({
      id: group.id,
      label: group.label,
    }));
  }, [plansByCategory]);

  const [activeClass, setActiveClass] = useState('');
  const [compactPlans, setCompactPlans] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px) and (max-width: 1279px)');
    const updateCompact = (event) => setCompactPlans(event.matches);
    setCompactPlans(mediaQuery.matches);
    mediaQuery.addEventListener('change', updateCompact);
    return () => mediaQuery.removeEventListener('change', updateCompact);
  }, []);

  useEffect(() => {
    if (tabs.length && !tabs.find((tab) => tab.id === activeClass)) {
      setActiveClass(tabs[0].id);
    }
  }, [tabs, activeClass]);

  const plans = useMemo(() => {
    return plansByCategory?.[activeClass]?.plans || [];
  }, [plansByCategory, activeClass]);

  const {
    open,
    selectedPlanId,
    setSelectedPlanId,
    openPack,
    closePack,
    handleProceed,
  } = useFoundationPackCheckout({
    plans,
    startPayment,
    isPlanSubscribed,
  });

  return (
    <section className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-16 lg:px-8">
      <div className="pointer-events-none absolute inset-0 z-0">
        <img
          src={learningplan}
          alt=""
          className="h-full w-full object-cover opacity-90"
          aria-hidden
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl pb-4">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl lg:text-4xl xl:text-[40px]">
            Choose Your CHSE Learning Plan
          </h2>
          <p className="mt-4 text-base font-medium text-[#1B1B1B] sm:text-lg lg:text-xl xl:text-[24px]">
            Simple plan for class 11 &amp; 12 students
          </p>
        </div>

        {(paymentError || paymentSuccess) && (
          <div className="mx-auto mt-6 max-w-2xl">
            {paymentError && (
              <p className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-700">
                {paymentError}
                <button
                  type="button"
                  onClick={clearPaymentFeedback}
                  className="ml-2 font-semibold underline"
                >
                  Dismiss
                </button>
              </p>
            )}
            {paymentSuccess && (
              <p className="rounded-lg bg-green-50 px-4 py-3 text-center text-sm text-green-700">
                {paymentSuccess}
                <button
                  type="button"
                  onClick={clearPaymentFeedback}
                  className="ml-2 font-semibold underline"
                >
                  Dismiss
                </button>
              </p>
            )}
          </div>
        )}

        {tabs.length > 0 && (
          <div className="mt-8 flex justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveClass(tab.id)}
                className={`rounded-lg px-5 py-2.5 text-sm font-bold tracking-wide transition sm:px-7 sm:text-base ${
                  activeClass === tab.id
                    ? 'bg-[#1A87CC] text-white shadow-md'
                    : 'bg-white text-[#999999] shadow-sm '
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="mt-10 flex justify-center gap-6 overflow-hidden">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`w-full max-w-[340px] shrink-0 animate-pulse rounded-2xl bg-white/60 xl:max-w-[380px] ${
                  compactPlans ? 'h-[450px]' : 'h-[545px]'
                }`}
              />
            ))}
          </div>
        ) : plans.length ? (
          <PricingPlansCarousel
            plans={plans}
            loading={loading}
            startPayment={openPack}
            payingPlanId={payingPlanId}
            isPlanSubscribed={isPlanSubscribed}
            compact={compactPlans}
            className="mt-10 lg:mt-12"
          />
        ) : (
          <p className="mt-10 text-center text-base font-medium text-[#666666]">
            No subscription plans available right now.
          </p>
        )}

        {/* <OfferBar /> */}
      </div>

      <div className="relative z-10 mt-6 w-full sm:mt-8">
        <FeatureBar />
      </div>

      <FoundationPackPopup
        open={open}
        plans={plans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onClose={closePack}
        onProceed={handleProceed}
        paying={Boolean(payingPlanId)}
        isPlanSubscribed={isPlanSubscribed}
      />
    </section>
  );
}

export default PricingSection;
